class ImageWithTextFloatingCta extends HTMLElement {
  constructor() {
    super();

    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleHostIntersection = this.handleHostIntersection.bind(this);
    this.handleTargetIntersection = this.handleTargetIntersection.bind(this);

    this.hasMovedToBody = false;
    this.hostObserver = null;
    this.targetObserver = null;
    this.hostVisible = true;
    this.targetVisible = false;
  }

  connectedCallback() {
    if (!this.hasMovedToBody) {
      this.hasMovedToBody = true;
      document.body.appendChild(this);
      return;
    }

    this.floatingLink = this.querySelector('[data-floating-cta-link]');

    if (!(this.floatingLink instanceof HTMLAnchorElement)) {
      return;
    }

    this.hostSection = document.getElementById(this.dataset.hostId || '');
    this.sourceLink = document.getElementById(this.dataset.sourceId || '');
    this.targetElement = this.getTargetElement();
    this.hideWhenTargetVisible = this.dataset.hideWhenTargetVisible === 'true'
      && this.targetElement instanceof HTMLElement;
    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');

    this.hostVisible = this.isElementVisible(this.hostSection);
    this.targetVisible = this.isElementVisible(this.targetElement);

    if (typeof this.mobileMediaQuery.addEventListener === 'function') {
      this.mobileMediaQuery.addEventListener('change', this.handleViewportChange);
    } else if (typeof this.mobileMediaQuery.addListener === 'function') {
      this.mobileMediaQuery.addListener(this.handleViewportChange);
    }

    this.setupObservers();
    this.updateVisibility();
  }

  disconnectedCallback() {
    if (typeof this.mobileMediaQuery?.removeEventListener === 'function') {
      this.mobileMediaQuery.removeEventListener('change', this.handleViewportChange);
    } else if (typeof this.mobileMediaQuery?.removeListener === 'function') {
      this.mobileMediaQuery.removeListener(this.handleViewportChange);
    }

    this.hostObserver?.disconnect();
    this.targetObserver?.disconnect();
    this.restoreSourceLink();
  }

  handleViewportChange() {
    this.updateVisibility();
  }

  handleHostIntersection(entries) {
    const [entry] = entries;
    if (!entry) return;

    this.hostVisible = entry.isIntersecting;
    this.updateVisibility();
  }

  handleTargetIntersection(entries) {
    const [entry] = entries;
    if (!entry) return;

    this.targetVisible = entry.isIntersecting;
    this.updateVisibility();
  }

  getTargetElement() {
    const targetId = this.dataset.targetId;
    if (!targetId) return null;

    return document.getElementById(targetId);
  }

  setupObservers() {
    if ('IntersectionObserver' in window) {
      if (this.hostSection instanceof HTMLElement) {
        this.hostObserver = new IntersectionObserver(this.handleHostIntersection, {
          threshold: 0
        });
        this.hostObserver.observe(this.hostSection);
      }

      if (this.hideWhenTargetVisible && this.targetElement instanceof HTMLElement) {
        this.targetObserver = new IntersectionObserver(this.handleTargetIntersection, {
          threshold: 0.1
        });
        this.targetObserver.observe(this.targetElement);
      }

      return;
    }

    this.hostVisible = this.isElementVisible(this.hostSection);
    this.targetVisible = this.isElementVisible(this.targetElement);
  }

  isElementVisible(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const rect = element.getBoundingClientRect();

    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  updateVisibility() {
    const shouldShow = this.mobileMediaQuery.matches
      && !this.hostVisible
      && (!this.hideWhenTargetVisible || !this.targetVisible);

    this.classList.toggle('is-visible', shouldShow);
    this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

    if (shouldShow) {
      this.floatingLink.removeAttribute('tabindex');
    } else {
      this.floatingLink.setAttribute('tabindex', '-1');
    }

    if (!(this.sourceLink instanceof HTMLAnchorElement)) {
      return;
    }

    if (shouldShow) {
      this.sourceLink.setAttribute('tabindex', '-1');
      return;
    }

    this.restoreSourceLink();
  }

  restoreSourceLink() {
    if (!(this.sourceLink instanceof HTMLAnchorElement)) {
      return;
    }

    this.sourceLink.removeAttribute('tabindex');
  }
}

if (!customElements.get('image-with-text-floating-cta')) {
  customElements.define('image-with-text-floating-cta', ImageWithTextFloatingCta);
}
